// src/components/QuickSortVisualizer.js
import React, { useState, useEffect } from 'react';
import '../styles/QuickSortVisualizer.css'; // Import specific CSS

function QuickSortVisualizer({ array, predefinedArray }) {
    const [steps, setSteps] = useState([]);
    const [currentStep, setCurrentStep] = useState(0);
    const [mode, setMode] = useState('manual');
    const [intervalId, setIntervalId] = useState(null);

    useEffect(() => {
        const data = predefinedArray || array;
        if (data.length > 0) {
            quickSort([...data], 0, data.length - 1);
        }
    }, [array, predefinedArray]);

    useEffect(() => {
        if (mode === 'automatic') {
            const id = setInterval(() => {
                setCurrentStep((prevStep) => {
                    if (prevStep < steps.length - 1) {
                        return prevStep + 1;
                    } else {
                        clearInterval(id);
                        return prevStep;
                    }
                });
            }, 2000); // 2 seconds
            setIntervalId(id);
            return () => clearInterval(id);
        }
    }, [mode, steps]);

    const quickSort = (arr, low, high) => {
        const stepsArray = [];
        const partition = (low, high) => {
            const pivot = arr[high];
            let i = low - 1;
            for (let j = low; j < high; j++) {
                if (arr[j] < pivot) {
                    i++;
                    [arr[i], arr[j]] = [arr[j], arr[i]];
                }
            }
            [arr[i + 1], arr[high]] = [arr[high], arr[i + 1]];
            return i + 1;
        };

        const sort = (low, high, stepNumber) => {
            if (low < high) {
                const pi = partition(low, high);
                stepsArray.push({
                    array: [...arr],
                    pivotIndex: pi,
                    explanation: `Step ${stepNumber}: Partitioning with pivot at index ${pi}. Current array state: [${arr.join(', ')}]`
                });
                sort(low, pi - 1, stepNumber + 1);
                sort(pi + 1, high, stepNumber + 1);
            }
        };

        sort(low, high, 1);
        setSteps(stepsArray);
    };

    const handleNextStep = () => {
        if (currentStep < steps.length - 1) {
            setCurrentStep(currentStep + 1);
        }
    };

    const handlePreviousStep = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
        }
    };

    const handleModeToggle = () => {
        if (mode === 'manual') {
            setMode('automatic');
        } else {
            setMode('manual');
            clearInterval(intervalId);
        }
    };

    const { array: currentArray, pivotIndex, explanation } = steps.length > 0 ? steps[currentStep] : { array: array, pivotIndex: -1, explanation: '' };

    return (
        <div>
            <h3>Quick Sort Visualization:</h3>
            <div className="quick-sort-container">
                <div className="quick-sort-section">
                    {currentArray.map((value, index) => (
                        <div
                            key={index}
                            className={`quick-sort-item ${index === pivotIndex ? 'pivot' : ''}`}
                            style={{
                                backgroundColor: index === pivotIndex ? '#ff5722' : undefined,
                                borderColor: index === pivotIndex ? '#ff5722' : '#007bff'
                            }}
                        >
                            {value}
                        </div>
                    ))}
                </div>
            </div>
            <div className="controls">
                <button onClick={handleModeToggle}>
                    {mode === 'manual' ? 'Start Automatic' : 'Start Manual'}
                </button>
                {mode === 'manual' && (
                    <>
                        <button onClick={handlePreviousStep}>Previous Step</button>
                        <button onClick={handleNextStep}>Next Step</button>
                    </>
                )}
            </div>
            <div className="step-details">
                <p>Current Step: {currentStep + 1} / {steps.length}</p>
                <p>{explanation}</p>
            </div>
            <div className="quick-sort-explanation">
                <h4>How QuickSort Works:</h4>
                <ol>
                    <li>
                        **Choose a Pivot**: Select a 'pivot' element from the array. The choice of pivot can vary: it can be the first element, the last element, the middle element, or a random element.
                    </li>
                    <li>
                        **Partitioning**: Reorder the array so that all elements less than the pivot come before it, and all elements greater than the pivot come after it. The pivot is now in its final sorted position.
                    </li>
                    <li>
                        **Recursive Sort**: Recursively apply the above steps to the sub-arrays of elements with values less than and greater than the pivot.
                    </li>
                    <li>
                        **Base Case**: The recursion terminates when the size of the sub-array is zero or one, as such arrays are already sorted.
                    </li>
                    <li>
                        **Combine Results**: Once all sub-arrays are sorted, combine them to obtain the fully sorted array.
                    </li>
                </ol>
                <p>
                    QuickSort is an efficient, comparison-based sorting algorithm that works by partitioning the array into smaller segments and sorting those segments independently. The average time complexity is O(n log n), but it can degrade to O(n^2) in the worst case, especially if the pivot choices are poor.
                </p>
            </div>
        </div>
    );
}

export default QuickSortVisualizer;
